import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Collapse,
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerOverlay,
  Flex,
  Image,
  Input,
  Spinner,
  Text,
  useDisclosure,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { AppBar, AppIcon, APP } from "../../components/mobileUi";
import {
  DesktopPage,
  DesktopPanel,
  DesktopSearch,
  DesktopTable,
  useIsDesktop,
} from "../../components/desktopUi";
import { appImages } from "../../assets/appicons";
import { getProjectListApi, getCityListApi } from "../../api/finance.project.api";
import { resolveImageUrl } from "../../utils/format";
import { projectSummary } from "../../utils/financeFormat";
import { EMP_BASE_PATH, EMP_API_BASE_URL } from "../../config";

const BORDER = "#E1E1E1";
const TEXT = "#08142D";
const MUTED = "#94A3B8";
const LIMIT = 10;

const SORT_OPTIONS = [
  { key: "NEWEST", label: "Từ mới đến cũ", sortOrder: 1 },
  { key: "OLDEST", label: "Từ cũ đến mới", sortOrder: 2 },
  { key: "HOT", label: "Đang hot", sortOrder: 3 },
];
const AGENT_OPTIONS = [
  { key: "ALL", label: "Tất cả độc quyền" },
  { key: "F0", label: "Độc quyền F0" },
  { key: "F1", label: "Độc quyền F1" },
];
const CITY_BADGE_COLORS = ["#F79D0D", "#E32A7A", "#4081EB"];

const ProjectCard = ({ item, onClick }) => {
  const img = item?.images?.[0]?.path
    ? resolveImageUrl(item.images[0].path, EMP_API_BASE_URL)
    : appImages.image_project_example;
  const badgeColor = useMemo(
    () => CITY_BADGE_COLORS[Math.floor(Math.random() * CITY_BADGE_COLORS.length)],
    []
  );
  const s = projectSummary(item?.apartment);

  return (
    <Box as="button" type="button" onClick={onClick} textAlign="left" w="100%" bg="white" border="1px solid" borderColor={BORDER} borderRadius="12px" overflow="hidden">
      <Image src={img} alt="" w="100%" h="180px" objectFit="cover" />
      <Box p="16px">
        {item?.city?.name && (
          <Box display="inline-block" bg={badgeColor} borderRadius="4px" px="6px" py="4px" mb="8px">
            <Text fontSize="11px" fontWeight={600} color="white">{item.city.name}</Text>
          </Box>
        )}
        <Text fontSize="16px" fontWeight={600} color={TEXT} noOfLines={1}>{item?.name || "-"}</Text>
        <Flex align="center" gap="8px" mt="8px" color={APP.primary200} fontWeight={600} fontSize="15px">
          <Text>{s.totalArea}</Text>
          <Box w="4px" h="4px" borderRadius="full" bg={APP.primary200} />
          <Text>{s.totalApartment}</Text>
          <Box w="4px" h="4px" borderRadius="full" bg={APP.primary200} />
          <Text noOfLines={1}>{s.totalValueRange}</Text>
        </Flex>
        <Text fontSize="14px" color={MUTED} mt="8px" noOfLines={1}>{item?.address || "-"}</Text>
      </Box>
    </Box>
  );
};

const FieldBox = ({ label, value, onClick }) => (
  <Box flex="1" minW="0">
    <Text fontSize="13px" color={TEXT} mb="6px">{label}</Text>
    <Flex as="button" type="button" onClick={onClick} align="center" justify="space-between" bg="#F7F9FC" borderRadius="8px" h="40px" px="10px" w="100%">
      <Text fontSize="14px" color={TEXT} noOfLines={1}>{value}</Text>
      <img src={appImages.icon_chevron_down} alt="" width={18} height={18} style={{ objectFit: "contain" }} />
    </Flex>
  </Box>
);

const ProjectList = () => {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const [filterOpen, setFilterOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [city, setCity] = useState({ _id: "all", name: "Tất cả" });
  const [agent, setAgent] = useState("ALL");
  const [sortKey, setSortKey] = useState("NEWEST");

  const [cities, setCities] = useState([]);
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(false);

  const sheet = useDisclosure();
  const [sheetType, setSheetType] = useState(null); // CITY | AGENT | SORT
  const reqId = useRef(0);
  const searchTimer = useRef(null);

  const sortOption = SORT_OPTIONS.find((x) => x.key === sortKey) || SORT_OPTIONS[0];

  useEffect(() => {
    getCityListApi().then((d) => setCities(d?.items || d || [])).catch(() => {});
  }, []);

  const buildParams = useCallback(
    (p) => ({
      page: p,
      limit: LIMIT,
      cityId: city?._id && city._id !== "all" ? city._id : undefined,
      agent: agent !== "ALL" ? agent : undefined,
      type: sortOption.sortOrder,
      search: search || undefined,
    }),
    [city, agent, sortOption, search]
  );

  const fetchPage = useCallback(
    async (p, mode) => {
      reqId.current += 1;
      const rid = reqId.current;
      setLoading(true);
      try {
        const data = await getProjectListApi(buildParams(p));
        if (rid !== reqId.current) return;
        const list = data?.items || data || [];
        setItems((prev) => (mode === "append" ? [...prev, ...list] : list));
        setHasNext(!!data?.pagination?.hasNextPage);
        setPage(p);
      } catch {
        if (rid === reqId.current && mode !== "append") setItems([]);
      } finally {
        if (rid === reqId.current) setLoading(false);
      }
    },
    [buildParams]
  );

  // Reload trang đầu khi filter/sort/search đổi
  useEffect(() => {
    fetchPage(1, "replace");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [city._id, agent, sortKey, search]);

  const onSearchChange = (e) => {
    const v = e.target.value;
    if (searchTimer.current) clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => setSearch(v.trim()), 300);
  };

  const openSheet = (type) => {
    setSheetType(type);
    sheet.onOpen();
  };

  const sheetTitle = sheetType === "CITY" ? "Thành phố" : sheetType === "AGENT" ? "Độc quyền" : "Sắp xếp";
  const sheetData =
    sheetType === "CITY"
      ? [{ _id: "all", name: "Tất cả" }, ...cities].map((c) => ({ key: c._id, label: c.name, raw: c }))
      : sheetType === "AGENT"
      ? AGENT_OPTIONS.map((a) => ({ key: a.key, label: a.label, raw: a.key }))
      : SORT_OPTIONS.map((s) => ({ key: s.key, label: s.label, raw: s.key }));

  const isActive = (raw) => {
    if (sheetType === "CITY") return raw?._id === city?._id;
    if (sheetType === "AGENT") return raw === agent;
    return raw === sortKey;
  };
  const pickItem = (raw) => {
    if (sheetType === "CITY") setCity(raw);
    else if (sheetType === "AGENT") setAgent(raw);
    else setSortKey(raw);
    sheet.onClose();
  };

  const bottomSheet = (
    <Drawer isOpen={sheet.isOpen} onClose={sheet.onClose} placement="bottom">
      <DrawerOverlay bg="rgba(0,0,0,0.5)" />
      <DrawerContent borderTopRadius="16px" maxW="480px" mx="auto">
        <DrawerBody py="16px" px="16px">
          <Text fontSize="16px" fontWeight={700} color={TEXT} mb="4px">
            {sheetTitle}
          </Text>
          <Box maxH="50vh" overflowY="auto">
            {sheetData.map((it, i) => {
              const active = isActive(it.raw);
              const last = i === sheetData.length - 1;
              return (
                <Flex
                  key={String(it.key)}
                  as="button"
                  w="100%"
                  align="center"
                  justify="space-between"
                  py="12px"
                  borderBottom={last ? "none" : "1px solid #EEF1F6"}
                  onClick={() => pickItem(it.raw)}
                >
                  <Text
                    fontSize="15px"
                    color={active ? APP.primary200 : TEXT}
                    fontWeight={active ? 600 : 400}
                  >
                    {it.label}
                  </Text>
                  {active && (
                    <img
                      src={appImages.icon_check}
                      alt=""
                      width={18}
                      height={18}
                      style={{ objectFit: "contain" }}
                    />
                  )}
                </Flex>
              );
            })}
          </Box>
          <Flex
            as="button"
            onClick={sheet.onClose}
            mt="12px"
            w="100%"
            h="46px"
            align="center"
            justify="center"
            borderRadius="10px"
            bg={APP.primary200}
            color="white"
            fontWeight={700}
          >
            Đóng
          </Flex>
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );

  // ===================== Bản WEB (desktop) =====================
  if (isDesktop) {
    const rows = items.map((it) => {
      const s2 = projectSummary(it?.apartment);
      return {
        id: it._id,
        img: it?.images?.[0]?.path
          ? resolveImageUrl(it.images[0].path, EMP_API_BASE_URL)
          : appImages.image_project_example,
        name: it?.name || "-",
        city: it?.city?.name || "-",
        area: s2.totalArea,
        apartment: s2.totalApartment,
        value: s2.totalValueRange,
        address: it?.address || "-",
      };
    });

    return (
      <DesktopPage title="Dự án">
        <DesktopPanel p="0">
          <Flex p="16px" gap="16px" flexWrap="wrap" align="flex-end">
            <Box w="320px">
              <Text fontSize="13px" color={TEXT} mb="6px">
                Tìm kiếm
              </Text>
              <DesktopSearch
                placeholder="Nhập tên dự án ..."
                onChange={onSearchChange}
                maxW="320px"
              />
            </Box>
            <Box w="220px">
              <Text fontSize="13px" color={TEXT} mb="6px">
                Tỉnh/Thành phố
              </Text>
              <Box
                as="button"
                w="100%"
                h="40px"
                px="12px"
                textAlign="left"
                bg="#F7F9FC"
                borderRadius="8px"
                fontSize="14px"
                onClick={() => openSheet("CITY")}
              >
                {city?.name || "Tất cả"}
              </Box>
            </Box>
            <Box w="220px">
              <Text fontSize="13px" color={TEXT} mb="6px">
                Độc quyền
              </Text>
              <Box
                as="button"
                w="100%"
                h="40px"
                px="12px"
                textAlign="left"
                bg="#F7F9FC"
                borderRadius="8px"
                fontSize="14px"
                onClick={() => openSheet("AGENT")}
              >
                {AGENT_OPTIONS.find((a) => a.key === agent)?.label || "Tất cả"}
              </Box>
            </Box>
            <Box w="200px">
              <Text fontSize="13px" color={TEXT} mb="6px">
                Sắp xếp
              </Text>
              <Box
                as="button"
                w="100%"
                h="40px"
                px="12px"
                textAlign="left"
                bg="#F7F9FC"
                borderRadius="8px"
                fontSize="14px"
                onClick={() => openSheet("SORT")}
              >
                {sortOption.label}
              </Box>
            </Box>
          </Flex>

          <DesktopTable
            loading={loading && !items.length}
            emptyText="Không có dự án"
            onRowClick={(row) => navigate(`${EMP_BASE_PATH}/tai-chinh/du-an/${row.id}`)}
            rows={rows}
            headers={[
              {
                key: "img",
                label: "Ảnh",
                width: "10%",
                render: (row) => (
                  <Image
                    src={row.img}
                    alt=""
                    w="72px"
                    h="48px"
                    objectFit="cover"
                    borderRadius="8px"
                    mx="auto"
                  />
                ),
              },
              { key: "name", label: "Dự án", align: "left", width: "22%" },
              { key: "city", label: "Tỉnh/Thành phố", width: "12%" },
              { key: "area", label: "Diện tích", width: "12%" },
              { key: "apartment", label: "Số căn", width: "10%" },
              { key: "value", label: "Khoảng giá", width: "14%" },
              { key: "address", label: "Địa chỉ", align: "left", width: "20%" },
            ]}
          />

          {hasNext && (
            <Flex
              as="button"
              onClick={() => fetchPage(page + 1, "append")}
              justify="center"
              py="16px"
              color={APP.primary200}
              fontWeight={600}
              fontSize="14px"
              w="100%"
            >
              {loading ? <Spinner size="sm" /> : "Xem thêm"}
            </Flex>
          )}
        </DesktopPanel>

        {bottomSheet}
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" pb="40px">
      <AppBar title="Dự án" onBack={() => navigate(EMP_BASE_PATH)} />

      <Box px="16px" pt="12px">
        {/* Search */}
        <Flex h="44px" align="center" px="12px" bg="white" border="1px solid" borderColor={BORDER} borderTopRadius="12px" borderBottom={filterOpen ? "0" : "1px solid"} borderBottomColor={BORDER} borderBottomRadius={filterOpen ? "0" : "12px"}>
          <AppIcon src={appImages.icon_search} size={20} color="#94A3B8" />
          <Input variant="unstyled" ml="8px" fontSize="14px" fontWeight={500} placeholder="Nhập tên dự án ..." onChange={onSearchChange} />
        </Flex>

        {/* Filter collapse header */}
        <Flex as="button" w="100%" h="44px" px="12px" align="center" justify="space-between" bg="white" border="1px solid" borderColor={BORDER} borderTop="0" borderBottomRadius={filterOpen ? "0" : "12px"} onClick={() => setFilterOpen((p) => !p)}>
          <Flex align="center" gap="8px">
            <img src={appImages.icon_filter_detail} alt="" width={18} height={18} style={{ objectFit: "contain" }} />
            <Text fontSize="14px" fontWeight={600} color={TEXT}>Bộ lọc chi tiết</Text>
          </Flex>
          <img src={filterOpen ? appImages.icon_chevron_up : appImages.icon_chevron_down} alt="" width={18} height={18} style={{ objectFit: "contain" }} />
        </Flex>
        <Collapse in={filterOpen} animateOpacity>
          <Box bg="white" border="1px solid" borderColor={BORDER} borderTop="0" borderBottomRadius="12px" p="12px">
            <Flex gap="10px">
              <FieldBox label="Tỉnh/Thành phố" value={city?.name || "Tất cả"} onClick={() => openSheet("CITY")} />
              <FieldBox label="Độc quyền" value={AGENT_OPTIONS.find((a) => a.key === agent)?.label || "Tất cả"} onClick={() => openSheet("AGENT")} />
            </Flex>
          </Box>
        </Collapse>

        {/* Header row + sort */}
        <Flex mt="16px" mb="14px" align="center" justify="space-between">
          <Text fontSize="14px" fontWeight={600} color={TEXT}>Danh sách dự án</Text>
          <Flex as="button" align="center" gap="6px" onClick={() => openSheet("SORT")}>
            <Text fontSize="14px" fontWeight={600} color={TEXT}>{sortOption.label}</Text>
            <img src={appImages.ion_sort_desc} alt="" width={16} height={16} style={{ objectFit: "contain" }} />
          </Flex>
        </Flex>

        {/* List */}
        {loading && !items.length ? (
          <Flex justify="center" py="40px"><Spinner color={APP.primary200} /></Flex>
        ) : items.length === 0 ? (
          <Text fontSize="14px" color={MUTED} textAlign="center" pt="40px">Không có dự án</Text>
        ) : (
          <Flex direction="column" gap="10px">
            {items.map((it) => (
              <ProjectCard key={it._id} item={it} onClick={() => navigate(`${EMP_BASE_PATH}/tai-chinh/du-an/${it._id}`)} />
            ))}
            {hasNext && (
              <Flex as="button" onClick={() => fetchPage(page + 1, "append")} justify="center" py="12px" color={APP.primary200} fontWeight={600} fontSize="14px">
                {loading ? <Spinner size="sm" /> : "Xem thêm"}
              </Flex>
            )}
          </Flex>
        )}
      </Box>

      {bottomSheet}
    </Box>
  );
};

export default ProjectList;
